import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Modal,
  Table,
  Checkbox,
  Button,
  Space,
  Typography,
  Card,
  Divider,
  Tag,
  message,
  Input,
  DatePicker,
} from 'antd';
import {
  FileExcelOutlined,
  EyeOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';
import { BankResponse } from '../../model/Bank.model';
import {
  getSearchCheckboxFilterProps,
  getSimpleCheckboxFilterProps,
} from '../../utils/table-filter-helper';
import {
  formatCardNumber,
  formatCurrency,
  getTotalDepositAmount,
  getTotalWithdrawalAmount,
  getBalanceDifference,
  getStatusText,
  getStatusColor,
} from './MaturityTableUtils';
import { exportMultipleMaturitySchedulesToExcel } from '../../utils/excelExport';
import MaturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import BankService from '../../service/bank-service/Bank.service';

// Status options khớp với DB enum (xem entity MaturitySchedule.status)
const STATUS_FILTER_OPTIONS = [
  { value: 'PENDING', label: 'Chưa làm' },
  { value: 'OVERDUE', label: 'Quá hạn' },
  { value: 'PAID', label: 'Đã nạp đáo' },
  { value: 'COMPLETED', label: 'Đã hoàn thành' },
  { value: 'ABANDONED', label: 'Không làm' },
];

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface MultiExportModalProps {
  visible: boolean;
  onClose: () => void;
  selectedMonth: number;
  selectedYear: number;
}

// Interface cho dữ liệu đã được làm giàu từ Backend
interface EnrichedMaturitySchedule extends MaturityScheduleResponse {
  customerName?: string;
  partnerName?: string;
}

const MultiExportModal: React.FC<MultiExportModalProps> = ({
  visible,
  onClose,
  selectedMonth,
  selectedYear,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [enrichedData, setEnrichedData] = useState<EnrichedMaturitySchedule[]>([]);
  const [banks, setBanks] = useState<BankResponse[]>([]);
  // CTV dropdown source (all CTV in the month, not just the current page).
  // Customer 2026-05-29: filter was "per page"; fix is to load all month-wide.
  const [monthPartners, setMonthPartners] = useState<string[]>([]);
  const [monthHasNoPartner, setMonthHasNoPartner] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // State cho bộ lọc và sắp xếp
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<Record<string, any>>({});
  const [searchedColumn, setSearchedColumn] = useState('');
  const [searchFilters, setSearchFilters] = useState<{
    keyword?: string;
    partnerName?: string;
    partnerNames?: string[];
    bankIds?: number[];
    statuses?: string[];
  }>({});
  const [partnerDropdownSearch, setPartnerDropdownSearch] = useState('');
  const searchInput = useRef<any>(null);
  const [tableKey, setTableKey] = useState(0);

  // Load dữ liệu từ Backend có phân trang
  const loadData = async (page: number, size: number, filters = searchFilters) => {
    setLoading(true);
    try {
      const response = await MaturityScheduleService.getExportDataPaginated(
        selectedMonth,
        selectedYear,
        page - 1, // Backend bắt đầu từ 0
        size,
        filters
      );

      const content = response.content || [];

      // Transform dữ liệu để hiển thị (tận dụng dữ liệu đã JOIN FETCH từ backend).
      // Customer 2026-05-29:
      //   - KHÔNG gán nhãn "KHÁCH LẺ" cho khách không có CTV (partnerName='').
      //   - DISPLAY (cột UI + cột Phí CTV trong Excel) thì show defaultFeeRate
      //     của thẻ — nhưng làm INLINE ở chỗ render, KHÔNG ghi đè trường
      //     commissionRateForPartner (giữ semantic gốc: % hoa hồng cho CTV,
      //     = 0 nếu không có CTV). Lý do: marginAmount Excel = withdrawal ×
      //     (feeRate - commissionRateForPartner)/100. Nếu set commissionRate
      //     = defaultFeeRate (≈ feeApplied), margin = 0 ⇒ công ty không giữ
      //     lại gì cho khách lẻ, sai bản chất nghiệp vụ.
      const transformedData = content.map((item: any) => {
        const hasPartner = !!item.creditCard?.customer?.partner;
        return {
          ...item,
          customerName: item.creditCard?.customer?.name || 'N/A',
          partnerName: hasPartner
            ? item.creditCard.customer.partner.name
            : '',
          commissionRateForPartner: hasPartner
            ? (item.creditCard.customer.commissionRateForPartner ||
                item.commissionRateForPartner ||
                0)
            : 0,
        };
      });

      setEnrichedData(transformedData);
      setPagination({
        current: page,
        pageSize: size,
        total: response.totalElements || 0,
      });
    } catch (error) {
      console.error('Error loading export data:', error);
      message.error('Không thể tải dữ liệu xuất Excel');
    } finally {
      setLoading(false);
    }
  };

  // CTV dropdown: month-wide list (not page-scoped). Always include KHÁCH LẺ
  // last when any partner-less customer exists this month — selecting it sends
  // "KHÁCH LẺ" to the backend, which interprets it as partner IS NULL.
  const partnerOptions = useMemo(
    () => {
      const opts = monthPartners
        .filter((name) => !!(name && name.trim()))
        .map((name) => ({ value: name, label: name }));
      if (monthHasNoPartner) {
        opts.push({ value: 'KHÁCH LẺ', label: 'KHÁCH LẺ' });
      }
      return opts;
    },
    [monthPartners, monthHasNoPartner]
  );

  useEffect(() => {
    if (visible) {
      loadData(1, pagination.pageSize);
      // Lazy-load danh sách ngân hàng cho filter dropdown (chỉ lần đầu mở modal)
      if (banks.length === 0) {
        BankService.getBankList()
          .then(setBanks)
          .catch((err) => console.error('Không thể tải danh sách ngân hàng:', err));
      }
      // Load month-wide CTV list (changes per month/year, so re-fetch on open)
      MaturityScheduleService.getDistinctPartners(selectedMonth, selectedYear)
        .then((res) => {
          setMonthPartners(res.partners || []);
          setMonthHasNoPartner(!!res.hasNoPartner);
        })
        .catch((err) => {
          console.error('Không thể tải danh sách CTV:', err);
          setMonthPartners([]);
          setMonthHasNoPartner(false);
        });
    } else {
      setEnrichedData([]);
      setSelectedRowKeys([]);
      setFilteredInfo({});
      setSortedInfo({});
      setTableKey(prev => prev + 1);
    }
  }, [visible, selectedMonth, selectedYear]);

  const bankOptions = useMemo(
    () => banks.map((b) => ({ value: b.id, label: b.name })),
    [banks]
  );

  const handleBankIdsChange = (bankIds: number[] = []) => {
    const newFilters = {
      ...searchFilters,
      bankIds: bankIds.length ? bankIds : undefined,
    };
    setSearchFilters(newFilters);
    loadData(1, pagination.pageSize, newFilters);
  };

  const handleStatusesChange = (statuses: string[] = []) => {
    const newFilters = {
      ...searchFilters,
      statuses: statuses.length ? statuses : undefined,
    };
    setSearchFilters(newFilters);
    loadData(1, pagination.pageSize, newFilters);
  };

  const handleTableChange = (newPagination: any, filters: any, sorter: any) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);

    // Nếu trang hoặc pageSize thay đổi thì gọi API
    if (newPagination.current !== pagination.current || newPagination.pageSize !== pagination.pageSize) {
      loadData(newPagination.current, newPagination.pageSize);
    }
  };

  // Hàm chọn tất cả các bản ghi ở mọi trang
  const handleSelectAllPages = async (checked: boolean) => {
    if (checked) {
      setLoading(true);
      try {
        const allIds = await MaturityScheduleService.getExportIds(selectedMonth, selectedYear, searchFilters);
        setSelectedRowKeys(allIds);
        message.success(`Đã chọn toàn bộ ${allIds.length} bản ghi của tháng`);
      } catch (error) {
        console.error('Error selecting all ids:', error);
        message.error('Không thể lấy danh sách ID');
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleClearAllFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setSearchFilters({});
    setPartnerDropdownSearch('');
    setTableKey((prev) => prev + 1);
    loadData(1, pagination.pageSize, {});
    message.success('Đã xóa tất cả bộ lọc');
  };

  const handleSearch = (selectedKeys: string[], confirm: any, dataIndex: string) => {
    confirm();
    setSearchedColumn(dataIndex);

    const value = (selectedKeys[0] || '').toString().trim();
    const newFilters = {
      ...searchFilters,
      ...(dataIndex === 'cardInfo' ? { keyword: value || undefined } : {}),
      ...(dataIndex === 'partnerName' ? { partnerName: value || undefined } : {}),
    };

    setSearchFilters(newFilters);
    loadData(1, pagination.pageSize, newFilters);
  };

  const handlePartnerNamesChange = (partnerNames: string[] = []) => {
    const newFilters = {
      ...searchFilters,
      partnerNames: partnerNames.length ? partnerNames : undefined,
      partnerName: undefined,
    };
    setSearchFilters(newFilters);
    loadData(1, pagination.pageSize, newFilters);
  };

  const previewData = useMemo(() => {
    const selectedRecords = enrichedData.filter((record) =>
      selectedRowKeys.includes(record.id)
    );

    return selectedRecords.reduce(
      (acc, record) => {
        const repayment = record.repaymentAmount || 0;
        const deposit = getTotalDepositAmount(record);
        const withdrawal = getTotalWithdrawalAmount(record);
        const fee = (repayment * (record.feeApplied || 0)) / 100;
        const ctvCommission = (withdrawal * (record.commissionRateForPartner || 0)) / 100;
        const balance = getBalanceDifference(record);

        return {
          count: acc.count + 1,
          totalRepayment: acc.totalRepayment + repayment,
          totalDeposit: acc.totalDeposit + deposit,
          totalWithdrawal: acc.totalWithdrawal + withdrawal,
          totalFee: acc.totalFee + fee,
          totalCtvCommission: acc.totalCtvCommission + ctvCommission,
          totalMargin: acc.totalMargin + (fee - ctvCommission),
          totalBalance: acc.totalBalance + balance,
        };
      },
      {
        count: 0,
        totalRepayment: 0,
        totalDeposit: 0,
        totalWithdrawal: 0,
        totalFee: 0,
        totalCtvCommission: 0,
        totalMargin: 0,
        totalBalance: 0,
      }
    );
  }, [enrichedData, selectedRowKeys]);

  const getColumnSearchProps = (dataIndex: string, placeholder: string): ColumnType<EnrichedMaturitySchedule> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Tìm
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) {
                clearFilters();
              }
              handleSearch([], confirm, dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Xóa
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Đóng
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns: ColumnsType<EnrichedMaturitySchedule> = [
    {
      title: 'Chọn',
      width: 60,
      fixed: 'left' as const,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, record.id]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id));
            }
          }}
        />
      ),
    },
    {
      title: 'Thông tin thẻ',
      key: 'cardInfo',
      width: 200,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <div className="space-y-1">
          <div className="font-medium text-blue-600 text-sm">
            {formatCardNumber(record.creditCard?.cardNumber)}
          </div>
          <div className="text-sm text-gray-800">
            <span className="text-gray-500">Chủ: </span>
            <span className="font-medium">{record.creditCard?.cardHolderName || 'N/A'}</span>
          </div>
          <div className="text-sm text-gray-800">
            <span className="text-gray-500">Khách: </span>
            <span className="font-medium text-green-600">{record.customerName}</span>
          </div>
        </div>
      ),
      ...getColumnSearchProps('cardInfo', 'Tìm thẻ / chủ thẻ / khách hàng...'),
    },
    {
      title: 'Ngân hàng',
      width: 140,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <div className="text-sm">
          <div className="font-medium">{record.creditCard?.bank?.name || 'N/A'}</div>
          <Tag color="gray">{record.creditCard?.cardType?.name || 'N/A'}</Tag>
        </div>
      ),
      ...getSearchCheckboxFilterProps(
        'Ngân hàng',
        bankOptions,
        searchFilters.bankIds || [],
        handleBankIdsChange
      ),
      filteredValue: searchFilters.bankIds?.length ? searchFilters.bankIds : null,
    },
    {
      title: 'Số tiền đáo',
      width: 130,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className="font-semibold text-blue-600">{formatCurrency(record.repaymentAmount)}</span>
      ),
      sorter: (a: any, b: any) => a.repaymentAmount - b.repaymentAmount,
    },
    {
      title: 'Đã nạp',
      width: 120,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className="font-semibold text-green-600">{formatCurrency(record.totalDepositAmount || 0)}</span>
      ),
    },
    {
      title: 'Phí (%)',
      width: 80,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span>{record.feeApplied ? `${record.feeApplied}%` : '-'}</span>
      ),
    },
    {
      title: 'CTV',
      width: 150,
      render: (_: any, record: EnrichedMaturitySchedule) => {
        // Customer 2026-05-29: "Phí" trong cột CTV hiển thị HOA HỒNG CHÊNH
        // LỆCH = Phí thẻ - Phí CTV. Khi không có CTV (commissionRateForPartner
        // = 0) thì marginRate = feeApplied = defaultFeeRate (đã được backend
        // safeguard đảm bảo). Khi có CTV, hiển thị phần công ty giữ lại sau
        // khi trừ phí CTV.
        const feeApplied = (record.feeApplied as number) || 0;
        const commissionRate = (record.commissionRateForPartner as number) || 0;
        const marginRate = feeApplied - commissionRate;
        return (
          <div className="text-sm">
            <div className="font-medium text-purple-600">{record.partnerName}</div>
            <div className="text-xs text-gray-500">Phí: {marginRate.toFixed(2)}%</div>
          </div>
        );
      },
      ...getSearchCheckboxFilterProps(
        'CTV',
        partnerOptions,
        searchFilters.partnerNames || [],
        handlePartnerNamesChange
      ),
      filteredValue: searchFilters.partnerNames?.length ? searchFilters.partnerNames : null,
    },
    {
      title: 'Hạn đáo',
      width: 110,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span>{dayjs(record.dueDate).format('DD/MM/YYYY')}</span>
      ),
      sorter: (a: any, b: any) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
    },
    {
      title: 'Trạng thái',
      width: 100,
      render: (_: any, record: EnrichedMaturitySchedule) => (
        <span className={getStatusColor(record.status)}>{getStatusText(record.status)}</span>
      ),
      ...getSimpleCheckboxFilterProps(
        'Trạng thái',
        STATUS_FILTER_OPTIONS,
        searchFilters.statuses || [],
        handleStatusesChange
      ),
      filteredValue: searchFilters.statuses?.length ? searchFilters.statuses : null,
    },
  ];

  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một kỳ đáo hạn để xuất Excel');
      return;
    }

    try {
      setLoading(true);
      // Bulk-fetch TẤT CẢ record đã chọn (cross-page). Trước đây chỉ filter từ
      // enrichedData (chỉ có data của trang hiện tại) -> "Chọn hiển thị" toàn bộ
      // 641 dòng nhưng Excel chỉ ra 20 dòng đầu tiên.
      const fullRecords = await MaturityScheduleService.getExportDataByIds(selectedRowKeys);
      // Same transform as loadData — partnerName='' and commissionRateForPartner=0
      // for no-CTV. The Excel export service displays defaultFeeRate INLINE for
      // those rows; the underlying field stays 0 so margin calc is correct.
      const enrichedRecords: EnrichedMaturitySchedule[] = fullRecords.map((item: any) => {
        const hasPartner = !!item.creditCard?.customer?.partner;
        return {
          ...item,
          customerName: item.creditCard?.customer?.name || 'N/A',
          partnerName: hasPartner
            ? item.creditCard.customer.partner.name
            : '',
          commissionRateForPartner: hasPartner
            ? (item.creditCard.customer.commissionRateForPartner ||
                item.commissionRateForPartner ||
                0)
            : 0,
        };
      });

      await exportMultipleMaturitySchedulesToExcel({
        records: enrichedRecords,
        month: selectedMonth,
        year: selectedYear,
      });

      message.success(`Đã xuất Excel thành công cho ${enrichedRecords.length} kỳ đáo hạn`);
      onClose();
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center space-x-2">
          <FileExcelOutlined className="text-green-600" />
          <span>Xuất Excel nhiều kỳ đáo hạn (Phân trang Backend)</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1300}
      footer={[
        <Button key="cancel" onClick={onClose}>Hủy</Button>,
        <Button
          key="export"
          type="primary"
          icon={<FileExcelOutlined />}
          onClick={handleExport}
          loading={loading}
          disabled={selectedRowKeys.length === 0}
        >
          Xuất Excel ({selectedRowKeys.length} kỳ đã chọn)
        </Button>,
      ]}
    >
      <div className="space-y-4">
        <Card size="small" className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <Text strong>Kỳ báo cáo: </Text>
              <Tag color="blue" className="text-base">Tháng {selectedMonth}/{selectedYear}</Tag>
            </div>
            <Text type="secondary">Tổng số bản ghi: {pagination.total}</Text>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Space size="large">
            <Checkbox
              checked={selectedRowKeys.length === pagination.total && pagination.total > 0}
              indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < pagination.total}
              onChange={(e) => handleSelectAllPages(e.target.checked)}
            >
              <span className="text-base">Chọn hiển thị</span>
            </Checkbox>
            <Text type="secondary" className="text-base">
              Đã chọn: {selectedRowKeys.length} kỳ | Hiển thị: {pagination.total}/{pagination.total}
            </Text>
          </Space>
          <Space>
            <Button icon={<ClearOutlined />} onClick={handleClearAllFilters} size="small">
              Xóa bộ lọc
            </Button>
            {loading && <Text type="secondary" className="text-xs">🔄 Đang tải...</Text>}
          </Space>
        </div>

        <Table
          key={tableKey}
          columns={columns}
          dataSource={enrichedData}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`,
          }}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ y: 450, x: 1200 }}
          size="small"
          className="border border-gray-100"
        />

        {selectedRowKeys.length > 0 && (
          <>
            <Divider />
            <Card
              size="small"
              title={
                <div className="flex items-center space-x-2">
                  <EyeOutlined />
                  <span>Xem trước tổng hợp</span>
                </div>
              }
              className="bg-green-50 border-green-200"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Text type="secondary">Số kỳ đáo hạn:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {previewData.count} kỳ
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng tiền đáo hạn:</Text>
                  <div className="text-lg font-semibold text-blue-600">
                    {formatCurrency(previewData.totalRepayment)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng đã nạp:</Text>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(previewData.totalDeposit)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng đã rút:</Text>
                  <div className="text-lg font-semibold text-orange-600">
                    {formatCurrency(previewData.totalWithdrawal)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalFee)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí CTV:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalCtvCommission)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Tổng phí HH:</Text>
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(previewData.totalMargin)}
                  </div>
                </div>
                <div>
                  <Text type="secondary">Chênh lệch:</Text>
                  <div
                    className={`text-lg font-semibold ${previewData.totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  >
                    {formatCurrency(previewData.totalBalance)}
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MultiExportModal;
